import { getGridStringOperators, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { useSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGroupsByParent } from "../groups/useGroupsByParent";
import { useHasPermission } from "../groups/useHasPermission";
import { useParkingOptions } from "../parking/useParkingOptions";
import { maintenanceSortComparator } from "./maintenanceSortComparator";
import { useDataGridSettings } from "@/components/DataGridSettingsProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { AddressCell } from "@/components/grid/cells/AddressCell";
import { CalibrationCell } from "@/components/grid/cells/CalibrationCell";
import { ChargeRateCell } from "@/components/grid/cells/ChargeRateCell";
import { ChargingCell } from "@/components/grid/cells/ChargingCell";
import { DateCellActivityHighlighted } from "@/components/grid/cells/DateCellActivityHighlighted";
import { DeviceActivityCell } from "@/components/grid/cells/DeviceActivityCell";
import { DiagnosticNameCell } from "@/components/grid/cells/DiagnosticNameCell";
import { DriverCell } from "@/components/grid/cells/DriverCell";
import { DrivingCell } from "@/components/grid/cells/DrivingCell";
import { FaultSourceCell } from "@/components/grid/cells/FaultSourceCell";
import { IgnitionCell } from "@/components/grid/cells/IgnitionCell";
import { MaintenanceCell } from "@/components/grid/cells/MaintenanceCell";
import { NameCell } from "@/components/grid/cells/NameCell";
import { OdometerCell } from "@/components/grid/cells/OdometerCell";
import ParkingCell from "@/components/grid/cells/ParkingCell";
import { PriorityCell } from "@/components/grid/cells/PriorityCell";
import { RemainingRangeCell } from "@/components/grid/cells/RemainingRangeCell";
import { RenderHeader } from "@/components/grid/cells/RenderHeader";
import { VehicleStatusCell } from "@/components/grid/cells/VehicleStatusCell";
import { WarningCell } from "@/components/grid/cells/WarningCell";
import ZoneCell from "@/components/grid/cells/ZoneCell";
import { drivingSortComparator } from "@/components/operators/DrivingSortComparator";
import { warningSortComparator } from "@/components/operators/WarningSortComparator";
import { WarningTypeFilterOperators } from "@/components/operators/WarningTypeFilterOperators";
import { ZoneFilterOperators } from "@/components/operators/ZoneFilterOperators";
import { VEHICLE_STATUS } from "@/constants/groupConfig";
import ParkingAddInData from "@/models/ParkingAddInData";
import { RemainingRangeAddInData } from "@/models/RemainingRangeAddInData";
import { VehicleStatus } from "@/models/VehicleStatusAddInData";
import Warning, { WarningType } from "@/models/Warning";

export function useDiagnosticColumns() {
  const { t } = useTranslation();
  const { pageType } = useDataGridSettings();
  const { me } = useGeotabClient();
  const { hasPermission: canDisplayMap, isError: permissionCheckFailed } =
    useHasPermission("DisplayMap");
  const { enqueueSnackbar } = useSnackbar();

  // The address column stays hidden while the permission is unknown; say why instead of
  // dropping it silently.
  useEffect(() => {
    if (permissionCheckFailed && !canDisplayMap) {
      enqueueSnackbar(t("permissions.checkFailed"), { variant: "warning", preventDuplicate: true });
    }
  }, [permissionCheckFailed, canDisplayMap, enqueueSnackbar, t]);
  const { parkingOptionsMap } = useParkingOptions();

  const { data: vehicleStatusGroups, isLoading: isLoadingVehicleStatus } =
    useGroupsByParent(VEHICLE_STATUS);

  const initialColumns = useMemo((): GridColDef[] => {
    if (isLoadingVehicleStatus) return [];
    const commonColumns: GridColDef[] = [
      {
        minWidth: 10,
        width: 300,
        field: "device.name",
        type: "string",
        headerName: t("faultOverview.deviceName", "Device Name"),
        renderHeader: RenderHeader,
        renderCell: DeviceActivityCell,
        valueGetter: (_, row) => row.device?.name,
      },
      {
        minWidth: 10,
        width: 150,
        field: "licensePlate",
        type: "string",
        headerName: t("faultOverview.licensePlate", "Licence Plate"),
        renderHeader: RenderHeader,
        renderCell: DiagnosticNameCell,
      },
      {
        minWidth: 10,
        width: 200,
        field: "statusData.dateTime",
        headerName: t("faultOverview.lastActivity", "lastActivity"),
        renderHeader: RenderHeader,
        type: "dateTime",
        renderCell: DateCellActivityHighlighted,
        valueGetter: (_, row) => row.statusData?.dateTime,
      },
      {
        minWidth: 10,
        width: 250,
        field: "zones",
        headerName: t("faultOverview.zones", "zones"),
        renderHeader: RenderHeader,
        renderCell: ZoneCell,
        filterOperators: getGridStringOperators()
          .filter(f => f.value === "contains")
          .concat(ZoneFilterOperators),
      },
      {
        minWidth: 10,
        width: 50,
        field: "isDriving",
        type: "boolean",
        headerName: t("faultOverview.isDriving"),
        renderHeader: RenderHeader,
        renderCell: DrivingCell,
        sortComparator: drivingSortComparator,
      },
      {
        minWidth: 10,
        width: 200,
        field: "driverId",
        headerName: t("faultOverview.driver"),
        renderHeader: RenderHeader,
        renderCell: DriverCell,
      },
      {
        minWidth: 10,
        width: 100,
        field: "ignitionStatus",
        headerName: t("faultOverview.ignitionStatus", "ignitionStatus"),
        renderHeader: RenderHeader,
        renderCell: IgnitionCell,
        type: "singleSelect",
        valueOptions: [
          { value: -1, label: "N/A" },
          { value: 0, label: t("off") },
          { value: 1, label: t("on") },
        ],
      },
      {
        minWidth: 10,
        width: 100,
        field: "isCharging",
        headerName: t("faultOverview.charging", "isCharging"),
        renderHeader: RenderHeader,
        renderCell: ChargingCell,
        type: "singleSelect",
        valueOptions: [
          { value: -1, label: "N/A" },
          { value: 0, label: t("faultOverview.doesntCharge") },
          { value: 1, label: t("faultOverview.charges") + " AC" },
          { value: 2, label: t("faultOverview.charges") + " DC" },
          { value: 3, label: t("faultOverview.chargeInterrupt") },
        ],
      },
    ];

    const diagnosticsViewColumns: GridColDef[] = [
      {
        minWidth: 10,
        width: 100,
        field: "faultPriority",
        headerName: t("faultOverview.faultPriority", "faultPriority"),
        renderCell: PriorityCell,
        renderHeader: RenderHeader,
        type: "singleSelect",
        valueOptions: [
          { value: 0, label: "N/A" },
          { value: 1, label: "Info" },
          { value: 2, label: t("priority.low") },
          { value: 3, label: t("priority.medium") },
          { value: 4, label: t("priority.high") },
        ],
        // For backwards compability, because original value was string
        valueGetter: value =>
          value === undefined || value === null || value === "N/A" ? 0 : value,

        // sortComparator: (v1, v2) => {
        //   if (!v1 || !v2) return 0;
        //   console.log(v1, v2);
        //   return v1 - v2;
        // },
      },
      {
        minWidth: 10,
        width: 150,
        field: "faultDescription",
        headerName: t("faultOverview.faultDescription", "faultDescription"),
        renderCell: NameCell,
        renderHeader: RenderHeader,
      },
      {
        minWidth: 10,
        width: 250,
        field: "faultName",
        headerName: t("faultOverview.faultName", "faultName"),
        renderCell: FaultSourceCell,
        renderHeader: RenderHeader,
      },
      {
        minWidth: 10,
        width: 100,
        field: "warnings",
        headerName: t("faultOverview.warningType"),
        renderCell: WarningCell,
        renderHeader: RenderHeader,
        valueFormatter: (value: Warning[]) => value.map(w => w.type),
        valueOptions: Object.values(WarningType),
        type: "singleSelect",
        filterOperators: WarningTypeFilterOperators,
        sortComparator: warningSortComparator,
      },
      {
        minWidth: 10,
        width: 150,
        field: "parking",
        headerName: t("faultOverview.parking"),
        renderCell: ParkingCell,
        renderHeader: RenderHeader,
        editable: true,
        type: "singleSelect",
        valueOptions: ({ row }) => {
          if (!row) return [{ value: "__NONE__", label: "---" }];
          const zoneNames = (row.allZones ?? []).map((z: { name: string }) => z?.name);
          const spots = zoneNames.flatMap((name: string) => parkingOptionsMap[name] ?? []);
          const uniqueSpots = [...new Set(spots)];
          return [
            { value: "__NONE__", label: "---" },
            ...uniqueSpots.map(s => ({ value: s, label: s })),
          ];
        },
        valueGetter: (value: ParkingAddInData | undefined) => value?.parkingSpace ?? "__NONE__",
        valueFormatter: (value: string) => {
          if (value === "__NONE__" || value == null) return "---";
          return value;
        },
        valueSetter: (value, row) => {
          const parkingSpace = value?.parkingSpace ?? value;
          if (parkingSpace === "__NONE__" || !parkingSpace) {
            return { ...row, parking: undefined };
          }
          return {
            ...row,
            parking: {
              deviceId: row.id,
              parkingSpace,
              timestamp: new Date(),
            } as ParkingAddInData,
          };
        },
      },
      {
        minWidth: 10,
        width: 100,
        field: "maintenance",
        headerName: t("faultOverview.maintenance"),
        renderCell: MaintenanceCell,
        renderHeader: RenderHeader,
        sortable: true,
        sortComparator: maintenanceSortComparator,
        filterable: true,
        filterOperators: [
          {
            label: t("faultOverview.filter.hasRequest"),
            value: "hasRequest",
            getApplyFilterFn: () => {
              return value => !!value?.workOrderRequest;
            },
          },
          {
            label: t("faultOverview.filter.hasJob"),
            value: "hasJob",
            getApplyFilterFn: () => {
              return value => {
                return !value?.workOrderRequest && !!value?.workOrderJob;
              };
            },
          },
          {
            label: t("faultOverview.filter.none"),
            value: "none",
            getApplyFilterFn: () => {
              return value => {
                return !value?.workOrderRequest && !value?.workOrderJob;
              };
            },
          },
        ],
      },
      {
        minWidth: 10,
        width: 200,
        field: "remarkInput",
        headerName: t("faultOverview.remark"),
        type: "string",
        editable: true,
        renderHeader: RenderHeader,
        valueGetter: (_, row) => row?.latestRemark ?? "",
      },
      {
        minWidth: 10,
        width: 250,
        field: "vehicleStatus",
        headerName: t("faultOverview.vehicleStatus", "status"),
        renderHeader: RenderHeader,
        renderCell: params => <VehicleStatusCell {...params} />,
        valueOptions: [
          { value: "__NONE__", label: t("common.none", "None") },
          ...vehicleStatusGroups?.map(g => ({ value: g.id, label: g.name })),
        ],

        valueGetter: (value: VehicleStatus) => {
          return value?.group?.id ?? "__NONE__";
        },

        valueFormatter: value => {
          if (value === "__NONE__" || value == null) {
            return "---";
          }
          const grp = vehicleStatusGroups.find(g => g.id === value);
          return grp?.name ?? "---";
        },
        valueSetter: (value, row) => {
          if (row.device.vehicleStatus === value) return row;
          return {
            ...row,
            vehicleStatus: {
              userId: me?.id,
              group: value?.group ?? value,
              deviceId: row.device.id,
              dateTime: new Date(),
            } as VehicleStatus,
          };
        },

        editable: true,
        type: "singleSelect",
      },

      {
        minWidth: 10,
        width: 100,
        field: "chargeRate",
        type: "number",
        headerName: t("faultOverview.chargingRate"),
        renderHeader: RenderHeader,
        valueGetter: (_, row) => (row.chargeRate !== null ? row.chargeRate / 1000 : null),
        renderCell: (props: GridRenderCellParams) => <ChargeRateCell props={props} />,
      },

      {
        minWidth: 10,
        width: 100,
        field: "odometer",
        type: "number",
        headerName: t("faultOverview.odometer"),
        renderHeader: RenderHeader,
        valueGetter: (_, row) => (row.odometer !== null ? row.odometer / 1000 : null),
        renderCell: (props: GridRenderCellParams) => <OdometerCell props={props} />,
      },

      {
        minWidth: 10,
        width: 100,
        field: "odometerAdjustment",
        type: "number",
        headerName: t("faultOverview.odometerAdjustment"),
        renderHeader: RenderHeader,
        valueGetter: (_, row) =>
          row.odometerAdjustment !== null ? row.odometerAdjustment / 1000 : null,
        renderCell: (props: GridRenderCellParams) => <OdometerCell props={props} />,
      },

      {
        minWidth: 10,
        width: 130,
        field: "remainingRange",
        type: "number",
        headerName: t("faultOverview.remainingRange"),
        renderHeader: RenderHeader,
        editable: true,
        renderCell: (props: GridRenderCellParams) => <RemainingRangeCell props={props} />,
        valueGetter: (_, row) => {
          const rr: RemainingRangeAddInData | undefined = row.remainingRange;
          if (!rr || row.odometer == null) return null;
          const drivenSinceInput = (row.odometer - rr.odometerAtInput) / 1000;
          return Math.max(0, Math.round(rr.inputKm - drivenSinceInput));
        },
        valueSetter: (value, row) => {
          const inputKm = typeof value === "number" ? value : parseFloat(value);
          if (isNaN(inputKm) || inputKm <= 0) {
            return { ...row, remainingRange: undefined };
          }
          return {
            ...row,
            remainingRange: {
              deviceId: row.id,
              inputKm,
              odometerAtInput: row.odometer ?? 0,
              dateTime: new Date(),
            } as RemainingRangeAddInData,
          };
        },
      },

      {
        minWidth: 10,
        width: 200,
        field: "calibration",
        type: "dateTime",
        headerName: t("faultOverview.calibration"),
        renderHeader: RenderHeader,
        valueGetter: (_, row) => {
          const data = (row.statusData?.statusData ?? []).find(
            (sd: { diagnostic: { id: string } }) => sd.diagnostic.id === "agOXwO-vH0EW66Ry_cjkJrg",
          )?.data;
          if (data == null || data === 0) return null;
          return new Date(data * 1000);
        },
        renderCell: CalibrationCell,
      },
    ];

    if (canDisplayMap) {
      commonColumns.push({
        minWidth: 10,
        width: 250,
        field: "address",
        headerName: t("faultOverview.address", "address"),
        renderHeader: RenderHeader,
        renderCell: AddressCell,
        filterOperators: getGridStringOperators()
          .filter(f => f.value === "contains")
          .concat(ZoneFilterOperators),
      });
    }

    return pageType === "diagnosticMonitor"
      ? [...commonColumns, ...diagnosticsViewColumns]
      : commonColumns;
  }, [
    isLoadingVehicleStatus,
    t,
    vehicleStatusGroups,
    pageType,
    me?.id,
    canDisplayMap,
    parkingOptionsMap,
  ]);
  return initialColumns;
}
